import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Typography,
  Stack,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { DESIGNATION_SECTIONS, type DesignationSection, type BillingMode } from '@altyor-kronos/shared-types';
import { CATALOG_SECTIONS, type CatalogSection } from '@altyor-kronos/shared-types';
import {
  isDesignationNameUnique,
  validateDesignationForm,
  type DesignationFormValues,
} from './designations';
import { useCatalogSnapshot, type CatalogRow } from './useCatalogSnapshot';
import { useHourlyRatesSnapshot } from './useHourlyRatesSnapshot';
import { formatCurrencyEUR } from './finance';

export interface ExistingDesignation {
  id: string;
  section: DesignationSection;
  category?: string;
  name: string;
  hoursSold: number;
  billingMode?: BillingMode;
  hourlyRateOverride?: number;
  initialOffer?: number;
  catalogId?: string | null;
  variant?: string | null;
  isCustom?: boolean;
}

interface Props {
  open: boolean;
  editing: ExistingDesignation | null;
  existing: readonly { id: string; name: string }[];
  onSubmit: (values: DesignationFormValues) => Promise<void>;
  onSubmitMultiple?: (values: DesignationFormValues[]) => Promise<void>;
  onClose: () => void;
}

const EMPTY: DesignationFormValues = {
  section: 'rd',
  category: '',
  name: '',
  hoursSold: '0',
  hourlyRateOverride: '',
  fixedAmount: '0',
  billingMode: 'hourly' as BillingMode,
  catalogId: null,
  variant: null,
  isCustom: false,
};

export default function DesignationDialog({
  open,
  editing,
  existing,
  onSubmit,
  onSubmitMultiple,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const hourlyRates = useHourlyRatesSnapshot();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm<DesignationFormValues>({ defaultValues: EMPTY });

  const [step, setStep] = useState<'pick' | 'variants' | 'details'>('pick');
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [selectedCatalogEntry, setSelectedCatalogEntry] = useState<CatalogRow | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<string[]>([]);
  const [selectedRateId, setSelectedRateId] = useState<string>('');

  const selectedSection = watch('section');
  const isCatalogSection = (CATALOG_SECTIONS as readonly string[]).includes(selectedSection);

  const { entries: catalogEntries } = useCatalogSnapshot(
    isCatalogSection ? (selectedSection as CatalogSection) : 'rd',
  );

  const existingNames = useMemo(
    () => new Set(existing.map((e) => e.name.toLowerCase())),
    [existing],
  );

  const availableEntries = useMemo(
    () => catalogEntries.filter((e) => !existingNames.has(e.data.name.toLowerCase())),
    [catalogEntries, existingNames],
  );

  useEffect(() => {
    if (open) {
      if (editing) {
        reset({
          section: editing.section,
          category: editing.category ?? '',
          name: editing.name,
          hoursSold: String(editing.hoursSold),
          hourlyRateOverride: editing.hourlyRateOverride != null ? String(editing.hourlyRateOverride) : '',
          fixedAmount: editing.initialOffer != null ? String(editing.initialOffer) : '0',
          billingMode: editing.billingMode ?? 'hourly',
          catalogId: editing.catalogId ?? null,
          variant: editing.variant ?? null,
          isCustom: editing.isCustom ?? false,
        });
        // Try to match existing override amount back to a named rate.
        const match = editing.hourlyRateOverride != null
          ? hourlyRates.find((r) => r.amount === editing.hourlyRateOverride)
          : undefined;
        setSelectedRateId(match?.id ?? '');
        setStep('details');
        setIsCustomMode(editing.isCustom ?? false);
        setSelectedCatalogEntry(null);
        setSelectedVariants([]);
      } else {
        reset(EMPTY);
        setStep('pick');
        setIsCustomMode(false);
        setSelectedCatalogEntry(null);
        setSelectedVariants([]);
        setSelectedRateId('');
      }
    }
  }, [open, editing, reset]);

  // Non-catalog sections (production, autres) have no catalog entries
  // to search — skip the pick step and go straight to custom mode.
  useEffect(() => {
    if (step === 'pick' && !editing && !isCatalogSection) {
      setIsCustomMode(true);
      setValue('catalogId', null);
      setValue('variant', null);
      setValue('isCustom', true);
      setValue('billingMode', selectedSection === 'rd' ? 'hourly' : 'fixed');
      setStep('details');
    }
  }, [step, editing, isCatalogSection, selectedSection, setValue]);

  const handleCatalogPick = (entry: CatalogRow | null) => {
    setSelectedCatalogEntry(entry);
    if (entry) {
      setValue('name', entry.data.name);
      setValue('catalogId', entry.id);
      setValue('isCustom', false);
      // billingMode is defined at catalog level and locked — PM cannot override.
      setValue('billingMode', entry.data.billingMode ?? 'hourly');
      setValue('hourlyRateOverride', '');
      setValue('fixedAmount', '0');
      setSelectedRateId('');
      if (entry.data.variants.length > 0) {
        setSelectedVariants([]);
        setStep('variants');
      } else {
        setValue('variant', null);
        setStep('details');
      }
    }
  };

  const handleVariantsConfirm = () => {
    if (selectedVariants.length === 1) {
      setValue('variant', selectedVariants[0]);
    }
    setStep('details');
  };

  const handleCustomMode = () => {
    setIsCustomMode(true);
    setValue('catalogId', null);
    setValue('variant', null);
    setValue('isCustom', true);
    setValue('billingMode', selectedSection === 'rd' ? 'hourly' : 'fixed');
    setValue('name', '');
    setValue('hourlyRateOverride', '');
    setValue('fixedAmount', '0');
    setSelectedRateId('');
    setSelectedCatalogEntry(null);
    setStep('details');
  };

  const onFormSubmit = handleSubmit(async (values) => {
    const validation = validateDesignationForm(values);
    let hasError = false;
    (['name', 'section', 'hoursSold', 'fixedAmount'] as const).forEach((field) => {
      if (validation[field]) {
        setError(field, { message: validation[field] });
        hasError = true;
      }
    });
    if (!hasError && !isDesignationNameUnique(values.name, existing, editing?.id)) {
      setError('name', { message: t('projects.designations.nameNotUnique') });
      hasError = true;
    }
    if (hasError) return;

    try {
      if (selectedVariants.length > 1 && onSubmitMultiple) {
        const multiValues = selectedVariants.map((v) => ({
          ...values,
          variant: v,
          name: values.name,
        }));
        await onSubmitMultiple(multiValues);
      } else {
        if (selectedVariants.length === 1) {
          values.variant = selectedVariants[0];
        }
        await onSubmit(values);
      }
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('projects.designations.saveFailed');
      setError('root', { message });
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing
          ? t('projects.designations.edit')
          : t('projects.designations.new')}
      </DialogTitle>
      <form onSubmit={onFormSubmit} noValidate>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {(errors as { root?: { message?: string } }).root?.message && (
              <Alert severity="error" data-testid="designation-dialog-error">
                {(errors as { root?: { message?: string } }).root!.message}
              </Alert>
            )}
            <Controller
              control={control}
              name="section"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('projects.designations.section')}
                  select
                  fullWidth
                  disabled={Boolean(editing) || step !== 'pick'}
                  error={Boolean(errors.section)}
                  helperText={errors.section?.message}
                >
                  {DESIGNATION_SECTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {t(`projects.designations.sections.${s}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {step === 'pick' && !editing && (
              <>
                {isCatalogSection && !isCustomMode && (
                  <Autocomplete
                    options={availableEntries}
                    getOptionLabel={(opt) => opt.data.name}
                    onChange={(_, value) => handleCatalogPick(value)}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Box sx={{ fontSize: 14 }}>{option.data.name}</Box>
                          {option.data.variants.length > 0 && (
                            <Box sx={{ fontSize: 11, opacity: 0.7 }}>
                              {option.data.variants.length} {t('projects.designations.variantsAvailable')}
                            </Box>
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('projects.designations.name')}
                        autoFocus
                        helperText={t('projects.designations.catalogPickHelp')}
                      />
                    )}
                  />
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleCustomMode}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {t('projects.designations.customDesignation')}
                </Button>
              </>
            )}

            {step === 'variants' && selectedCatalogEntry && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('projects.designations.selectVariants')}
                </Typography>
                <FormGroup>
                  {selectedCatalogEntry.data.variants.map((v) => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={selectedVariants.includes(v)}
                          onChange={(_, checked) => {
                            setSelectedVariants((prev) =>
                              checked ? [...prev, v] : prev.filter((x) => x !== v),
                            );
                          }}
                        />
                      }
                      label={v}
                    />
                  ))}
                </FormGroup>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleVariantsConfirm}
                  disabled={selectedVariants.length === 0}
                  sx={{ mt: 1 }}
                >
                  {t('common.continue')}
                </Button>
              </Box>
            )}

            {step === 'details' && (
              <>
                <TextField
                  label={t('projects.designations.name')}
                  fullWidth
                  autoFocus={isCustomMode}
                  {...register('name')}
                  disabled={!isCustomMode && !editing}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                />

                {selectedVariants.length > 0 && (
                  <Stack direction="row" spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      {t('projects.designations.selectedVariants')}:
                    </Typography>
                    <Typography variant="body2">
                      {selectedVariants.join(', ')}
                    </Typography>
                  </Stack>
                )}

                <Controller
                  control={control}
                  name="billingMode"
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>{t('projects.designations.billingMode.label')}</FormLabel>
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="hourly"
                          control={<Radio />}
                          label={t('projects.designations.billingMode.hourly')}
                          disabled={Boolean(editing) || Boolean(selectedCatalogEntry)}
                        />
                        <FormControlLabel
                          value="fixed"
                          control={<Radio />}
                          label={t('projects.designations.billingMode.fixed')}
                          disabled={Boolean(editing) || Boolean(selectedCatalogEntry)}
                        />
                      </RadioGroup>
                      {selectedCatalogEntry && (
                        <Typography variant="caption" color="text.secondary">
                          {t('projects.designations.billingModeLocked')}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />

                {watch('billingMode') === 'hourly' && (
                  <TextField
                    label={t('projects.designations.hoursSold')}
                    type="number"
                    fullWidth
                    slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                    {...register('hoursSold')}
                    error={Boolean(errors.hoursSold)}
                    helperText={errors.hoursSold?.message}
                  />
                )}
                {watch('billingMode') === 'hourly' && (
                  <TextField
                    select
                    label={t('projects.designations.hourlyRateOverrideLabel')}
                    fullWidth
                    value={selectedRateId}
                    onChange={(e) => {
                      const rateId = e.target.value;
                      setSelectedRateId(rateId);
                      const rate = hourlyRates.find((r) => r.id === rateId);
                      setValue('hourlyRateOverride', rate ? String(rate.amount) : '');
                    }}
                    helperText={t('projects.designations.hourlyRateOverrideHelp')}
                  >
                    <MenuItem value="">
                      <em>{t('projects.designations.hourlyRateNone')}</em>
                    </MenuItem>
                    {hourlyRates.map((rate) => (
                      <MenuItem key={rate.id} value={rate.id}>
                        {rate.label} — {formatCurrencyEUR(rate.amount, i18n.language)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {watch('billingMode') === 'fixed' && (
                  <TextField
                    label={t('projects.designations.fixedAmount')}
                    type="number"
                    fullWidth
                    slotProps={{ htmlInput: { min: 0, step: 100 } }}
                    {...register('fixedAmount')}
                    error={Boolean(errors.fixedAmount)}
                    helperText={errors.fixedAmount?.message ?? t('projects.designations.fixedAmountHelp')}
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="secondary" disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          {step === 'details' && (
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
              {t('common.save')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
